import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { 
  X, 
  ChevronRight, 
  ChevronLeft,
  Sparkles,
  Plus,
  MousePointer,
  Layers,
  Play,
  Download,
  CheckCircle
} from 'lucide-react';

/**
 * Quick Start Tutorial - Guided Onboarding Overlay
 * 
 * Shows first-time users how to:
 * 1. Create their first machine
 * 2. Add states and transitions
 * 3. Define capabilities & effects
 * 4. Run simulation
 * 5. Export their work
 */

const TUTORIAL_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to FIRMASTATE',
    description: 'A powerful IDE for authoring, validating, and compiling state machines with capability-typed effects.',
    icon: Sparkles,
    highlight: null,
    action: null
  },
  {
    id: 'create',
    title: 'Create Your First Machine',
    description: 'Click "New Machine" in the workspace panel or use ⌘K to open the command palette. Give your machine a name and an initial state.',
    icon: Plus,
    highlight: '[data-testid="new-machine-btn"], button:has-text("New Machine")',
    action: 'Click "New Machine" to continue'
  },
  {
    id: 'graph',
    title: 'Visual Graph Editor',
    description: 'Your machine appears as an interactive graph. Click on state nodes to select them. Use the toolbar to add states, transitions, and more.',
    icon: MousePointer,
    highlight: '[data-testid="center-surface-container"]',
    action: 'Explore the graph view'
  },
  {
    id: 'capabilities',
    title: 'Capabilities & Effects',
    description: 'Switch to the "Caps & Effects" tab to define what your machine can do. Effects require capabilities - this is the "constitutional layer" of your machine.',
    icon: Layers,
    highlight: 'button:has-text("Caps & Effects")',
    action: 'Open the Caps & Effects panel'
  },
  {
    id: 'simulate',
    title: 'Run Simulation',
    description: 'Use the "Simulate" tab to test your machine. Dispatch events, see transitions fire, and travel through the execution timeline.',
    icon: Play,
    highlight: 'button:has-text("Simulate")',
    action: 'Try the simulation engine'
  },
  {
    id: 'export',
    title: 'Export Your Work',
    description: 'When ready, use the "Export" tab to generate TypeScript code, JSON schemas, or graph visualizations for your machine.',
    icon: Download,
    highlight: 'button:has-text("Export")',
    action: 'Export your machine'
  },
  {
    id: 'complete',
    title: 'You\'re Ready!',
    description: 'You now know the basics. Explore keyboard shortcuts (press ⌘K), check the Inspector panel for details, and build amazing state machines!',
    icon: CheckCircle,
    highlight: null,
    action: null
  }
];

const STORAGE_KEY = 'smf_tutorial_completed';

const QuickStartTutorial = ({ onComplete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasSeenTutorial, setHasSeenTutorial] = useState(true);

  useEffect(() => {
    // Check if user has already completed the tutorial
    const completed = localStorage.getItem(STORAGE_KEY);
    if (!completed) {
      setHasSeenTutorial(false);
      // Show tutorial after a short delay
      const timer = setTimeout(() => setIsOpen(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleNext = () => {
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsOpen(false);
    setHasSeenTutorial(true);
    onComplete?.();
  };

  const handleSkip = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setIsOpen(false);
    setHasSeenTutorial(true);
  };

  const handleRestart = () => {
    setCurrentStep(0);
    setIsOpen(true);
  };

  if (!isOpen) {
    // Show a small "Help" button if tutorial was dismissed but not completed
    if (!hasSeenTutorial) {
      return (
        <Button
          variant="outline"
          size="sm"
          onClick={handleRestart}
          className="fixed bottom-4 right-4 z-50 bg-blue-600/20 border-blue-500/30 hover:bg-blue-600/30"
          data-testid="restart-tutorial-btn"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Quick Start
        </Button>
      );
    }
    return null;
  }

  const step = TUTORIAL_STEPS[currentStep];
  const StepIcon = step.icon;
  const progress = ((currentStep + 1) / TUTORIAL_STEPS.length) * 100;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="quick-start-tutorial">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={handleSkip}
      />
      
      {/* Tutorial Card */}
      <div className="relative bg-[#0a0e27] border border-white/20 rounded-xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
        {/* Progress Bar */}
        <div className="h-1 bg-white/10">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center space-x-2">
            <Badge variant="outline" className="bg-blue-500/20 text-blue-400 border-blue-500/30">
              {currentStep + 1} / {TUTORIAL_STEPS.length}
            </Badge>
            <span className="text-xs text-gray-500">Quick Start</span>
          </div>
          <button 
            onClick={handleSkip}
            className="p-1 hover:bg-white/10 rounded transition-colors"
            data-testid="close-tutorial-btn"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-8">
          <div className="flex flex-col items-center text-center space-y-4">
            {/* Icon */}
            <div className={cn(
              "w-16 h-16 rounded-full flex items-center justify-center",
              currentStep === TUTORIAL_STEPS.length - 1 
                ? "bg-green-500/20" 
                : "bg-blue-500/20"
            )}>
              <StepIcon className={cn(
                "w-8 h-8",
                currentStep === TUTORIAL_STEPS.length - 1 
                  ? "text-green-400" 
                  : "text-blue-400"
              )} />
            </div>

            {/* Title */}
            <h2 className="text-xl font-bold text-white">{step.title}</h2>

            {/* Description */}
            <p className="text-gray-400 max-w-md">{step.description}</p>

            {/* Action hint */}
            {step.action && (
              <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 mt-4">
                <span className="text-xs text-blue-400">{step.action}</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-white/10 bg-black/20">
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePrev}
            disabled={currentStep === 0}
            className="text-gray-400"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>

          <div className="flex items-center space-x-2">
            {currentStep < TUTORIAL_STEPS.length - 1 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSkip}
                className="text-gray-500"
              >
                Skip Tutorial
              </Button>
            )}
            <Button
              onClick={handleNext}
              size="sm"
              className={cn(
                currentStep === TUTORIAL_STEPS.length - 1 
                  ? "bg-green-600 hover:bg-green-700" 
                  : "bg-blue-600 hover:bg-blue-700"
              )}
              data-testid="tutorial-next-btn"
            >
              {currentStep === TUTORIAL_STEPS.length - 1 ? (
                <>
                  Get Started
                  <CheckCircle className="w-4 h-4 ml-2" />
                </>
              ) : (
                <>
                  Next
                  <ChevronRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Step Dots */}
        <div className="flex items-center justify-center space-x-1.5 pb-4">
          {TUTORIAL_STEPS.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentStep(idx)}
              className={cn(
                "w-2 h-2 rounded-full transition-all",
                idx === currentStep 
                  ? "bg-blue-500 w-4" 
                  : idx < currentStep 
                    ? "bg-blue-500/50" 
                    : "bg-white/20"
              )}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default QuickStartTutorial;
